import { FC, HTMLInputTypeAttribute } from "react"
import { Control, Controller, FieldError, FieldValues, UseFormRegister } from "react-hook-form"
import WarningSvg from '@/shared/assets/images/Warning.svg'
import ReactSelect, { GroupBase, OptionsOrGroups } from "react-select"
import ArrowDown from '@/shared/assets/images/ArrowDown.svg'
import DatePicker from "react-datepicker"
import CalendarIcon from "@/shared/assets/images/CalendarBlank.svg"
import { SelectComponents } from "react-select/dist/declarations/src/components"

interface DatepickerForm {
    control: Control<FieldValues>
    label: string,
    id: string,
    type: "datepicker"
    required: boolean
    placeholder?: string
    error?: FieldError
}

interface SelectForm {
    control: Control<FieldValues>
    label: string,
    id: string,
    isMulti?: boolean
    type: "select"
    required: boolean
    placeholder?: string
    noOptionsMessage?: (obj: {
        inputValue: string;
    }) => React.ReactNode
    error?: FieldError
    options: OptionsOrGroups<any, GroupBase<any>>
    components?: Partial<SelectComponents<any, boolean, GroupBase<any>>>
}

interface DefaultForm {
    register: UseFormRegister<FieldValues>
    label: string,
    id: string,
    type?: HTMLInputTypeAttribute
    required: boolean
    placeholder?: string
    error?: FieldError
    min?: number
    max?: number
    maxLength?: number
    minLength?: number
}

const DefaultView: FC<DefaultForm> = ({ register, min, max, minLength, maxLength, placeholder, error, type = "text", required, id, label }) => {
    return (
        <div className={`flex flex-col gap-[8px] pb-[8px] mb-2 border-b-[1.5px] border-[#B9C9C6] ${error && 'border-color-danger'}`}>
            <div className="flex justify-between w-full items-center">
                <label className="font-semibold uppercase text-[12px] text-[#828282]">
                    {label}
                </label>
                {error &&
                    <label className="text-[12px] flex items-center text-color-danger">
                        <WarningSvg width={20} height={20} className="me-1" />
                        {error.message}
                    </label>
                }
            </div>
            <input 
                placeholder={placeholder}
                type={type}
                min={min}
                max={max}
                minLength={minLength}
                maxLength={maxLength}
                className={`w-full bg-transparent outline-0 text-[13px] [&::placeholder]:text-color-dark/70 [&::placeholder]:text-base outline-none text-color-dark`}
                {...register(id, { required })} 
            />
        </div>
    )
}

const SelectView: FC<SelectForm> = ({ placeholder, isMulti, error, control, options, id, required, label, components, noOptionsMessage }) => {
    return (
        <div className={`flex flex-col gap-[8px] pb-[8px] mb-2 border-b-[1.5px] border-[#B9C9C6] ${error && 'border-color-danger'}`}>
            <div className="flex justify-between w-full items-center">
                <label className="font-semibold uppercase text-[13px] text-[#828282]">
                    {label}
                </label>
                {error &&
                    <label className="text-[12px] flex items-center text-color-danger">
                        <WarningSvg width={20} height={20} className="me-1" />
                        {error.message}
                    </label>
                }
            </div>
            <Controller
                control={control}
                name={id}
                render={({ field }) => (
                <ReactSelect 
                    {...field}
                    classNames={{
                        control: () => 'w-full !outline-0 !bg-transparent !shadow-none !min-h-[auto] !border-0 !border-none !p-0 !outline-none ',
                        valueContainer: () => '!border-0 text-[13px] !outline-0 !p-0 text-color-dark text-lg',
                        placeholder: () => 'text-color-dark/70 text-base !m-0 text-color-dark',
                        menu: () => '!left-[0]',
                        input: () => '!m-0 !p-0'
                    }}
                    isMulti={isMulti}
                    name={field.name}
                    classNamePrefix="react_select_custom"
                    ref={field.ref}
                    noOptionsMessage={
                        noOptionsMessage
                        ? noOptionsMessage
                        : () => 'Нет опций'
                    }
                    value={isMulti ? options.filter(option => field.value?.find((i: any) => i === option.value)) : (options.find(option => option.value === field.value) || "")}
                    onChange={(e) => isMulti ? field.onChange(e.map((i: any) => i.value)) : field.onChange(e.value)}
                    options={options}
                    placeholder={placeholder}
                    required={required}
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                    components={{
                        DropdownIndicator: () => <ArrowDown height={20} width={20}/>,
                        IndicatorSeparator: () => null,
                        ...components
                    }}
                />)}
            />
        </div>
    )
}

const DatepickerView: FC<DatepickerForm> = ({ placeholder, error, control, id, required, label }) => {
    return (
        <div className={`flex flex-col gap-[8px] pb-[8px] mb-2 border-b-[1.5px] border-[#B9C9C6] ${error && 'border-color-danger'}`}>
            <div className="flex justify-between w-full items-center">
                <label className="font-semibold uppercase text-base text-[#828282]">
                    {label}
                </label>
                {error &&
                    <label className="text-base flex items-center text-color-danger">
                        <WarningSvg width={20} height={20} className="me-1" />
                        {error.message}
                    </label>
                }
            </div>
            <Controller
                control={control}
                name={id}
                render={({ field }) => (
                    <DatePicker 
                        placeholderText={placeholder}
                        required={required}
                        selected={field.value}
                        wrapperClassName="react-datepicker-form"
                        dateFormat={"dd.MM.yyyy"}
                        showIcon
                        onChange={(date: Date) => field.onChange(date)}
                        enableTabLoop={false}
                        popperProps={{
                            placement: 'bottom',
                        }}
                        portalId="root"
                        icon={(<CalendarIcon width={24} height={24}/>)}
                    />
                )}
            />
        </div>
    )
}

type FormInputProps = DefaultForm | DatepickerForm | SelectForm

export const FormbuilderInput: FC<FormInputProps> = (props) => {
    switch(props.type) {
    case 'datepicker':
        return <DatepickerView {...props as DatepickerForm} />
    case 'select':
        return <SelectView {...props as SelectForm} />
    default:
        return <DefaultView {...props as DefaultForm} />
    }
}